import {
  chargeElavonCard,
  chargeElavonOnline,
  chargeSavedCard,
  getElavonOnlineCredentials
} from 'api/Transaction';

import { showAlert } from 'components/shared/Alert/Alert';

const DEFAULT_ERROR_MESSAGE =
  "There's been an error. Please try again later. If the issue persists please contact support.";

export const elavonSavedCardHandler = (
  navigate,
  { invoices, appointmentId, surchargeAmount, amount, appliedBalance, invoiceToken, methodDetail }
) => {
  if (!methodDetail) return;

  return chargeSavedCard(navigate, {
    amount,
    invoiceToken,
    invoices,
    appointmentId,
    cardId: methodDetail,
    surchargeAmount,
    appliedBalance
  });
};

const completeTransaction = async (
  navigate,
  { token, invoices, invoiceToken, transactionId, response, payerId, appliedBalance }
) => {
  try {
    const res = await chargeElavonOnline(navigate, {
      token,
      invoices,
      invoiceToken,
      transactionId,
      transaction: response,
      payerId,
      appliedBalance
    });

    const { code, redirect, error, cardId: _cardId } = res;
    switch (code) {
      case -1:
        navigate(redirect);
        break;
      case 0: {
        return res;
      }
      default:
        showAlert({ title: error || DEFAULT_ERROR_MESSAGE, color: 'danger' });
        break;
    }
  } catch (error) {
    console.error(error);
    showAlert({ title: error || DEFAULT_ERROR_MESSAGE, color: 'danger' });
  }
};

const openLightbox = async (
  navigate,
  token,
  transactionId,
  { invoices, invoiceToken, payerId, appliedBalance, tokenOnly, preAuthForm, terminalId }
) => {
  return new Promise((resolve, reject) => {
    try {
      const callback = {
        async onError(error) {
          try {
            if (tokenOnly) {
              reject(new Error(DEFAULT_ERROR_MESSAGE));
              return;
            }

            const result = await completeTransaction(navigate, {
              token,
              invoices,
              invoiceToken,
              transactionId,
              payerId,
              appliedBalance,
              response: { txnStatus: 'error', ...error }
            });
            reject(result);
          } catch (err) {
            reject(err);
          }
        },
        async onCancelled() {
          try {
            if (tokenOnly) {
              return;
            }

            const result = await completeTransaction(navigate, {
              token,
              invoices,
              invoiceToken,
              transactionId,
              payerId,
              appliedBalance,
              response: { txnStatus: 'cancelled' }
            });
            resolve(result);
          } catch (err) {
            reject(err);
          }
        },
        async onDeclined(response) {
          try {
            if (tokenOnly) {
              reject(new Error('Payment was declined.'));
              return;
            }

            const result = await completeTransaction(navigate, {
              token,
              invoices,
              invoiceToken,
              transactionId,
              payerId,
              appliedBalance,
              response: { txnStatus: 'declined', ...response }
            });
            reject(result);
          } catch (err) {
            reject(err);
          }
        },
        async onApproval(response) {
          try {
            if (tokenOnly) {
              const result = await chargeElavonCard(navigate, {
                terminalId,
                tokenOnly,
                invoiceToken,
                payerId,
                onlineCard: { ...response, terminal_id: terminalId },
                preAuthForm
              });
              resolve(result);
              return;
            }

            const result = await completeTransaction(navigate, {
              token,
              invoices,
              invoiceToken,
              transactionId,
              payerId,
              appliedBalance,
              terminalId,
              response: { txnStatus: 'approved', ...response }
            });
            resolve(result);
          } catch (err) {
            reject(err);
          }
        }
      };

      window.PayWithConverge.open({ ssl_txn_auth_token: token }, callback);
    } catch (error) {
      console.error(error);
      showAlert({
        title: error || DEFAULT_ERROR_MESSAGE,
        color: 'danger'
      });
      reject(error);
    }
  });
};

export const chargeElavonOnlineHandler = async (
  navigate,
  {
    amount,
    saveCard,
    tokenOnly,
    billingAddress,
    invoiceToken,
    surchargeAmount,
    payerId,
    patientId,
    appliedBalance,
    invoices,
    transactionId,
    preAuthForm,
    terminalId
  }
) => {
  try {
    const credentialsResponse = await getElavonOnlineCredentials(navigate, {
      amount,
      saveCard,
      tokenOnly,
      billingAddress,
      invoiceToken,
      surcharge: surchargeAmount,
      payerId,
      appliedBalance,
      patientId,
      terminalId
    });
    const credentialsData = credentialsResponse?.data || credentialsResponse;

    if (credentialsData?.code === 0) {
      const finalTransactionId = credentialsData.transactionId || transactionId;
      return openLightbox(navigate, credentialsData.token, finalTransactionId, {
        invoices,
        invoiceToken,
        payerId,
        appliedBalance,
        tokenOnly,
        saveCard,
        preAuthForm,
        terminalId
      });
    }

    throw new Error(credentialsData?.error || DEFAULT_ERROR_MESSAGE);
  } catch (error) {
    console.error('Error in chargeElavonOnlineHandler:', error);
    throw new Error(error?.message || DEFAULT_ERROR_MESSAGE);
  }
};
